/**
 * toContactRow — pure adapter, `CombinePreviewContact` → `ImportContactRow`
 * (Combine & Filter round 2, sub-task R2-B).
 *
 * Lets "Review contacts" reuse the SAME `ContactsTable` the People-list
 * detail page renders (`variant="preview"`, `richPreview`), instead of the
 * bespoke table `PreviewTable.tsx` used to hand-roll — same Name/avatar
 * rendering, same Thrive ID cell, same drag-resize base columns.
 *
 * Every field `CombinePreviewContact` does not carry gets a NULL / neutral
 * value here, never a fabricated one — `ContactsTable` already treats every
 * one of them as optional-and-absent-safe (a Company-Map-only column, an
 * export outcome that has not happened yet). The two fields the preview
 * response DOES carry that the detail row's Thrive-ID cell depends on
 * (`contact_id_thrive`, `compared_at`/`thrive_match_type`) are derived from
 * `thrive_status` rather than copied 1:1 — see the switch below for why.
 */
import type { ImportContactRow } from "@nu-assist/contracts";

import type { CombinePreviewContact } from "../../../clients/imports.client";

export function toContactRow(c: CombinePreviewContact): ImportContactRow {
  // `hasCompareVerdict` (ContactsTable's own predicate) reads
  // `compared_at != null || thrive_match_type != null` to decide "has this
  // row ever been classified" — which for the detail page means "did Compare
  // run on THIS row". The preview's own concept is `thrive_status`, computed
  // per the ACTIVE `remove_in_thrive` mode rather than per row-level Compare
  // history, so it is translated into the same two fields rather than copied:
  // a non-null `thrive_match_type` with no `compared_at` is the exact shape
  // ContactsTable already renders for a legacy pre-0040 verdict ("date not
  // recorded"), which is the honest reading here too — the preview knows
  // WHETHER a source was checked, not WHEN.
  const contact_id_thrive =
    c.thrive_status === "in_search" || c.thrive_status === "in_thrive" ? c.contact_id_thrive : null;
  const thrive_match_type = c.thrive_status === "not_found" ? "new" : null;

  return {
    import_contact_id: c.import_contact_id,
    first_name: c.first_name ?? null,
    last_name: c.last_name ?? null,
    headline: c.headline ?? null,
    location: c.location ?? null,
    linkedin_url: c.linkedin_url ?? null,
    connection_degree: null,
    current_position: c.current_position ?? null,
    current_company: c.current_company ?? null,
    profile_image_url: null,
    profile_image_s3_key: c.profile_image_s3_key,
    profile_image_captured_at: c.profile_image_captured_at ?? null,
    thrive_avatar_url: c.thrive_avatar_url,
    summary: null,
    primary_company: null,
    email: c.email ?? null,
    email_source: null,
    work_email: null,
    work_email_source: null,
    enrichment_status: null,
    enrichment_error: null,
    contact_id_thrive,
    thrive_match_type,
    // No per-row compare timestamp in the preview response — see the
    // docblock above. `ContactsTable` renders "date not recorded" rather
    // than inventing one.
    compared_at: null,
    export_status: null,
    export_error: null,
    // Never left out / a duplicate-LinkedIn warning here: those are FIRM-WIDE
    // registry facts the preview response does not compute, and defaulting
    // them false/unset keeps `isExportable` (the selection-checkbox gate)
    // reading every row as selectable, which is what "cherry-pick before
    // Generate" needs.
    is_w2: false,
    is_w3: false,
    left_out: false,
    left_out_at: null,
    review_status: "not_compared",
    duplicate_linkedin: false,
    different_person_thrive_ids: [],
    thrive_id_state: null,
    add_to_search_state: null,
    skipped: false,
    exported_at: null,
    bct_match_reason: null,
    // The person's resolved Thrive stage (05), read only while the
    // "Candidacy stage" filter is on.
    candidacy_stage: c.candidacy_stage ?? null,
    positions: [],
    annex: {},
  };
}
